import React, { Component } from 'react';
import { Query } from 'react-apollo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import '../../style/ShareLinks.scss';
import queries from '../../queries';
import { apolloClient, shareBaseUrl, backEndUri } from '../../config';
import { showToast, timeAgo } from '../../toast';
import { t } from '../../i18n';
import confirmDialog from '../dialogs/ConfirmDialog';
import ImageUploader from '../uploaders/ImageUploader';

// Même règle que le serveur : minuscules, chiffres et tirets.
const toSlug = (value) => String(value || '')
  .trim().toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

// Les codes d'erreur renvoyés par le serveur sont traduits ici.
const errorMessage = (error) => {
  const code = error && error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].extensions && error.graphQLErrors[0].extensions.code;
  const known = { INVALID_SLUG: 'ui.share.err_invalid', RESERVED_SLUG: 'ui.share.err_reserved', SLUG_TAKEN: 'ui.share.err_taken' };
  return known[code] ? t(known[code]) : t('ui.share.err_generic');
};

// Liens de partage d'une interface : https://treeosk.co/preview/<nom>/ ouvre l'aperçu public, sans fichier à envoyer.
class ShareLinks extends Component {

  state = { slug: '', busy: false, editing: null, copied: null, retrying: null, check: null, checking: false, drafts: {} };

  refetch = () => [{ query: queries.shareLinks.listForProject, variables: { id: this.props.match.params.projectId } }];

  create = async () => {
    const slug = toSlug(this.state.slug);
    if (slug.length < 3) { showToast(t('ui.share.err_invalid'), 'error', 5000); return; }
    this.setState({ busy: true });
    try {
      await apolloClient.mutate({
        mutation: queries.shareLinks.create,
        variables: { input: { project_id: this.props.match.params.projectId, slug } },
        refetchQueries: this.refetch,
      });
      this.setState({ slug: '', busy: false });
      showToast(t('ui.share.created'));
    } catch (e) {
      this.setState({ busy: false });
      showToast(errorMessage(e), 'error', 6000);
    }
  };

  patch = async (input, successKey) => {
    try {
      await apolloClient.mutate({ mutation: queries.shareLinks.update, variables: { input }, refetchQueries: this.refetch });
      if (successKey) showToast(t(successKey), 'info', 2500);
      return true;
    } catch (e) {
      showToast(errorMessage(e), 'error', 6000);
      return false;
    }
  };

  retry = async (link) => {
    this.setState({ retrying: link.id });
    try {
      const res = await apolloClient.mutate({ mutation: queries.shareLinks.publish, variables: { input: link.id }, refetchQueries: this.refetch });
      const status = res.data.publishShareLink.remote_status;
      showToast(status === 'ok' ? t('ui.share.remote_ok_toast') : t('ui.share.remote_fail_toast'), status === 'ok' ? 'success' : 'error', 5000);
    } catch (e) {
      showToast(errorMessage(e), 'error', 6000);
    }
    this.setState({ retrying: null });
  };

  runCheck = async () => {
    this.setState({ checking: true, check: null });
    try {
      const res = await apolloClient.query({ query: queries.shareLinks.ftpCheck, fetchPolicy: 'network-only' });
      this.setState({ check: res.data.shareFtpCheck, checking: false });
    } catch (e) {
      this.setState({ check: { configured: true, ok: false, error: t('ui.share.err_generic'), entries: [] }, checking: false });
    }
  };

  remove = async (link) => {
    try {
      await confirmDialog(t('ui.share.delete_confirm', { slug: link.slug }));
    } catch (e) { return; }
    await apolloClient.mutate({ mutation: queries.shareLinks.del, variables: { input: link.id }, refetchQueries: this.refetch });
    showToast(t('ui.share.deleted'), 'info');
  };

  copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const el = document.createElement('textarea');
      el.value = text; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el);
    }
    this.setState({ copied: id });
    setTimeout(() => this.setState(({ copied }) => (copied === id ? { copied: null } : null)), 1800);
  };


  // Fiche vitrine : brouillon local par lien, enregistré d'un coup avec le bouton « Enregistrer la fiche ».
  draft = (link) => this.state.drafts[link.id] || {
    showcase_name: link.showcase_name || '', showcase_label: link.showcase_label || '', showcase_subtitle: link.showcase_subtitle || '',
    showcase_description: link.showcase_description || '', showcase_solution: link.showcase_solution || '',
    objectives_text: (link.showcase_objectives || []).join('\n'),
  };

  setDraft = (link, key, value) => this.setState(({ drafts }) => ({ drafts: { ...drafts, [link.id]: { ...this.draft(link), [key]: value } } }));

  saveShowcase = async (link) => {
    const d = this.draft(link);
    const ok = await this.patch({
      id: link.id,
      showcase: {
        showcase_name: d.showcase_name, showcase_label: d.showcase_label, showcase_subtitle: d.showcase_subtitle,
        showcase_description: d.showcase_description, showcase_solution: d.showcase_solution,
        showcase_objectives: d.objectives_text.split('\n'),
      },
    }, 'ui.share.showcase_saved');
    if (ok) this.setState(({ drafts }) => { const next = { ...drafts }; delete next[link.id]; return { drafts: next }; });
  };

  setGallery = (link, index, uri) => {
    const gallery = [...(link.showcase_gallery || [])];
    if (uri) gallery[index] = uri; else gallery.splice(index, 1);
    return this.patch({ id: link.id, showcase: { showcase_gallery: gallery.filter(Boolean) } }, 'ui.share.saved');
  };

  renderShowcase(link) {
    const d = this.draft(link);
    const gallery = link.showcase_gallery || [];
    const field = (key, label, props = {}) => (
      <label className='tsk-showcase__field'>
        <span>{label}</span>
        {props.multiline
          ? <textarea rows={props.rows || 4} value={d[key]} onChange={(e) => this.setDraft(link, key, e.target.value)} placeholder={props.placeholder} />
          : <input type='text' value={d[key]} onChange={(e) => this.setDraft(link, key, e.target.value)} placeholder={props.placeholder} />}
      </label>
    );
    return (
      <details className='tsk-sharelink__bg tsk-showcase'>
        <summary>{t('ui.share.showcase')}</summary>
        <p className='tsk-settings-hint'>{t('ui.share.showcase_hint')}</p>
        {field('showcase_name', t('ui.share.showcase_name'), { placeholder: 'Haribo' })}
        {field('showcase_label', t('ui.share.showcase_label'), { placeholder: 'Semaine 47' })}
        {field('showcase_subtitle', t('ui.share.showcase_subtitle'), { placeholder: 'Jackpot en magasin' })}
        {field('showcase_solution', t('ui.share.showcase_solution'), { placeholder: 'Borne sur mesure' })}
        {field('showcase_description', t('ui.share.showcase_description'), { multiline: true, rows: 5 })}
        {field('objectives_text', t('ui.share.showcase_objectives'), { multiline: true, rows: 4, placeholder: t('ui.share.showcase_objectives_ph') })}
        <button type='button' className='tsk-button' onClick={() => this.saveShowcase(link)}>{t('ui.share.showcase_save')}</button>

        <h4 className='tsk-showcase__h'>{t('ui.share.showcase_cover')}</h4>
        <p className='tsk-settings-hint'>{t('ui.share.showcase_cover_hint')}</p>
        <ImageUploader
          value={link.showcase_cover_uri}
          canDelete
          onUpload={(uri) => this.patch({ id: link.id, showcase: { showcase_cover_uri: uri } }, 'ui.share.saved')}
          onDelete={() => this.patch({ id: link.id, showcase: { showcase_cover_uri: '' } }, 'ui.share.saved')}
        />

        <h4 className='tsk-showcase__h'>{t('ui.share.showcase_gallery')}</h4>
        <p className='tsk-settings-hint'>{t('ui.share.showcase_gallery_hint')}</p>
        <div className='tsk-showcase__gallery'>
          {gallery.map((uri, i) => (
            <ImageUploader key={`${uri}-${i}`} value={uri} canDelete onUpload={(u) => this.setGallery(link, i, u)} onDelete={() => this.setGallery(link, i, '')} />
          ))}
          {gallery.length < 6 && <ImageUploader key={`new-${gallery.length}`} value={null} onUpload={(u) => this.setGallery(link, gallery.length, u)} />}
        </div>
      </details>
    );
  }

  renderLink(link) {
    const { editing, copied } = this.state;
    const siteUrl = `${shareBaseUrl}${link.slug}/`;
    const backup = `${backEndUri}/preview/${link.slug}/`;
    const status = link.remote_status;
    // Tant que la page n'est pas publiée sur treeosk.co, le lien à partager est celui de l'API (il marche tout de suite).
    const url = status === 'ok' ? siteUrl : backup;
    return (
      <li key={link.id} className={`tsk-sharelink${link.active ? '' : ' is-off'}`}>
        <div className='tsk-sharelink__top'>
          <span className={`tsk-badge ${link.active ? 'tsk-badge--ok' : 'tsk-badge--idle'}`}>
            <span className='tsk-badge__dot' />{link.active ? t('ui.share.active') : t('ui.share.inactive')}
          </span>
          <span className='tsk-sharelink__date'>{t('ui.share.created_at', { when: timeAgo(link.created_at) })}</span>
        </div>

        <div className='tsk-sharelink__url'>
          <input type='text' readOnly value={url} onFocus={(e) => e.target.select()} aria-label={t('ui.share.link')} />
          <button type='button' className='tsk-button' onClick={() => this.copy(url, link.id)}>
            <FontAwesomeIcon icon={['fas', copied === link.id ? 'check-circle' : 'copy']} /> {copied === link.id ? t('ui.share.copied') : t('ui.share.copy')}
          </button>
          <a className='tsk-button tsk-sharelink__open' href={url} target='_blank' rel='noopener noreferrer'>
            <FontAwesomeIcon icon={['fas', 'external-link-alt']} /> {t('ui.share.open')}
          </a>
        </div>

        {status === 'ok' && (
          <p className='tsk-sharelink__remote is-ok'>
            <FontAwesomeIcon icon={['fas', 'check-circle']} /> {t('ui.share.remote_ok')}
            <span className='tsk-sharelink__alt'> · {t('ui.share.backup')} <a href={backup} target='_blank' rel='noopener noreferrer'>{backup}</a></span>
          </p>
        )}
        {status === 'error' && (
          <p className='tsk-sharelink__remote is-error'>
            <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} /> {t('ui.share.remote_error', { error: link.remote_error || '' })}
            {' '}
            <button type='button' className='tsk-button tsk-small' disabled={this.state.retrying === link.id} onClick={() => this.retry(link)}>
              {this.state.retrying === link.id ? t('ui.share.retrying') : t('ui.share.retry')}
            </button>
          </p>
        )}
        {(status === 'none' || status === 'not_configured') && (
          <p className='tsk-sharelink__remote'>
            {status === 'not_configured' ? t('ui.share.remote_not_configured', { url: siteUrl }) : t('ui.share.remote_pending', { url: siteUrl })}
            {' '}
            <button type='button' className='tsk-button tsk-small' disabled={this.state.retrying === link.id} onClick={() => this.retry(link)}>
              {this.state.retrying === link.id ? t('ui.share.retrying') : t('ui.share.publish_now')}
            </button>
          </p>
        )}
        {status === 'off' && <p className='tsk-sharelink__remote'>{t('ui.share.remote_off')}</p>}

        <div className='tsk-sharelink__actions'>
          <label className='tsk-sharelink__toggle'>
            <input type='checkbox' checked={link.active} onChange={(e) => this.patch({ id: link.id, active: e.target.checked }, e.target.checked ? 'ui.share.enabled' : 'ui.share.disabled')} />
            <span>{t('ui.share.active_toggle')}</span>
          </label>
          {editing && editing.id === link.id ? (
            <span className='tsk-sharelink__rename'>
              <input type='text' autoFocus value={editing.slug} onChange={(e) => this.setState({ editing: { id: link.id, slug: toSlug(e.target.value) } })} />
              <button type='button' className='tsk-button tsk-small' onClick={async () => { if (await this.patch({ id: link.id, slug: editing.slug }, 'ui.share.renamed')) this.setState({ editing: null }); }}>{t('ui.share.save')}</button>
              <button type='button' className='tsk-button tsk-link tsk-small' onClick={() => this.setState({ editing: null })}>{t('ui.common.cancel')}</button>
            </span>
          ) : (
            <button type='button' className='tsk-button tsk-link tsk-small' onClick={() => this.setState({ editing: { id: link.id, slug: link.slug } })}>{t('ui.share.rename')}</button>
          )}
          <button type='button' className='tsk-button tsk-link tsk-small tsk-sharelink__delete' onClick={() => this.remove(link)}>
            <FontAwesomeIcon icon='trash-alt' /> {t('ui.common.delete')}
          </button>
        </div>

        <details className='tsk-sharelink__bg'>
          <summary>{t('ui.share.background')}</summary>
          <p className='tsk-settings-hint'>{t('ui.share.background_hint')}</p>
          <ImageUploader
            value={link.background_uri}
            canDelete
            onUpload={(uri) => this.patch({ id: link.id, background_uri: uri }, 'ui.share.saved')}
            onDelete={() => this.patch({ id: link.id, background_uri: '' }, 'ui.share.saved')}
          />
        </details>

        {this.renderShowcase(link)}
      </li>
    );
  }

  render() {
    const { projectId } = this.props.match.params;
    const { slug, busy } = this.state;
    const clean = toSlug(slug);
    return (
      <div className='tsk-page-board'>
        <header className='tsk-page-hero'>
          <h1 className='tsk-page-hero__title'>{t('ui.share.title')}</h1>
          <p className='tsk-page-hero__subtitle'>{t('ui.share.lead')}</p>
        </header>

        <section className='tsk-creation-card tsk-share-new'>
          <h2 className='tsk-creation-step'><span><FontAwesomeIcon icon='plus' /></span> {t('ui.share.new')}</h2>
          <div className='tsk-share-new__row'>
            <label htmlFor='tsk-share-slug'>{t('ui.share.name_label')}</label>
            <div className='tsk-share-new__field'>
              <span className='tsk-share-new__prefix'>{shareBaseUrl}</span>
              <input
                id='tsk-share-slug'
                type='text'
                value={slug}
                placeholder='pwc-2026'
                onChange={(e) => this.setState({ slug: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') this.create(); }}
              />
              <button type='button' className='tsk-button' disabled={busy || clean.length < 3} onClick={this.create}>{t('ui.share.create')}</button>
            </div>
            <p className='tsk-settings-hint'>
              {clean ? t('ui.share.preview_url', { url: `${shareBaseUrl}${clean}/` }) : t('ui.share.name_hint')}
            </p>
          </div>
        </section>

        <section className='tsk-share-check'>
          <button type='button' className='tsk-button tsk-link tsk-small' disabled={this.state.checking} onClick={this.runCheck}>
            <FontAwesomeIcon icon={['fas', 'sync-alt']} spin={this.state.checking} /> {t('ui.share.check')}
          </button>
          {this.state.check && (
            <div className={`tsk-share-check__result${this.state.check.ok ? ' is-ok' : ' is-error'}`}>
              {!this.state.check.configured && <p>{t('ui.share.check_not_configured')}</p>}
              {this.state.check.configured && !this.state.check.ok && <p>{t('ui.share.check_error', { error: this.state.check.error || '' })}</p>}
              {this.state.check.ok && (
                <>
                  <p>{this.state.check.missing ? t('ui.share.check_missing', { dir: this.state.check.missing }) : t('ui.share.check_ok', { dir: this.state.check.dir })}</p>
                  <ul>{(this.state.check.entries || []).slice(0, 30).map((e) => <li key={e}>{e}</li>)}</ul>
                </>
              )}
            </div>
          )}
        </section>

        <Query query={queries.shareLinks.listForProject} variables={{ id: projectId }} fetchPolicy='cache-and-network'>
          {({ data }) => {
            const links = (data && data.project && data.project.share_links) || null;
            if (!links) return <p>{t('ui.common.loading')}</p>;
            if (links.length === 0) {
              return (
                <div className='tsk-empty'>
                  <FontAwesomeIcon icon={['fas', 'share']} className='tsk-empty__icon' />
                  <h3>{t('ui.share.empty_title')}</h3>
                  <p>{t('ui.share.empty_text')}</p>
                </div>
              );
            }
            return <ul className='tsk-sharelinks'>{links.map((link) => this.renderLink(link))}</ul>;
          }}
        </Query>
      </div>
    );
  }
}

export default ShareLinks;
